import React, { useState, useRef, useEffect } from 'react';
import { FiBell } from 'react-icons/fi';
import { Link, useNavigate } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationsContext';
import { routeForNotification } from '../../../utils/notificationRoute';

function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

// `light` renders a white bell for dark header backgrounds.
export default function NotificationBell({ light = false }) {
  const { items, unread, markRead, markAllRead, remove } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const handleClick = (n) => {
    if (!n.read_at) markRead(n.id);
    setOpen(false);
    const dest = routeForNotification(n);
    if (/^https?:\/\//i.test(dest)) {
      window.open(dest, '_blank', 'noopener,noreferrer');
    } else {
      navigate(dest);
    }
  };

  const recent = items.slice(0, 10);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`relative p-2 ${light ? 'text-white/90 hover:text-white' : 'text-gray-600 hover:text-gray-900'}`}
        aria-label="Notifications"
      >
        <FiBell className="w-5 h-5" />
        {unread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] leading-none rounded-full min-w-[16px] h-4 px-1 flex items-center justify-center">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-100">
            <span className="font-semibold text-sm text-black">Notifications</span>
            {unread > 0 && (
              <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">
                Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {recent.length === 0 ? (
              <p className="px-4 py-8 text-sm text-gray-400 text-center">No notifications yet.</p>
            ) : (
              recent.map((n) => (
                <div
                  key={n.id}
                  onClick={() => handleClick(n)}
                  className={`group px-4 py-3 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${n.read_at ? '' : 'bg-blue-50/40'}`}
                >
                  <div className="flex items-start gap-2">
                    {!n.read_at && <span className="mt-1.5 w-2 h-2 rounded-full bg-blue-500 flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-black truncate">{n.title}</p>
                      {n.body && <p className="text-xs text-gray-600 line-clamp-2">{n.body}</p>}
                      <p className="text-[11px] text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                      className="flex-shrink-0 -mr-1 px-1 text-lg leading-none text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Delete notification"
                      title="Delete"
                    >
                      ×
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
          <Link
            to="/dashboard/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-sm text-blue-600 py-2 hover:underline border-t border-gray-100"
          >
            View all
          </Link>
        </div>
      )}
    </div>
  );
}
